import { ref } from 'vue'
import { type MatrixEvent, NotificationCountType, type Room } from 'matrix-js-sdk'
import type { ChatTopicsDisplayMode, IChat, IChatTopic } from '@/modules/chat-matrix-fast/types/IChat'
import { CHAT_TYPES } from '@/modules/chat-matrix-fast/constants/chat'
import {
  getMatrixRoomLastMessage,
} from '@/modules/chat-matrix-fast/services/matrixChatClient'
import { getMatrixClient } from '@/modules/chat-matrix-fast/services/matrixClientLifecycle'
import { isMatrixReadMarkerAtLastReadable } from '@/modules/chat-matrix-fast/services/matrixReadReceiptService'
import { topicsDebugLog } from '@/modules/chat-matrix-fast/utils/chatDebugLog'

const LOCALLY_READ_TOPIC_ROOMS_STORAGE_KEY = 'chat-matrix:locally-read-topic-rooms'
const TOPICS_DISPLAY_STORAGE_PREFIX = 'chat-matrix-topics-display-'

/** Инкремент при смене личного режима список/табы — для реактивного UI. */
const topicsDisplayPreferenceRevision = ref(0)

/** Pending read этого клиента, пока ждём Matrix sync. Не источник истины для unread. */
const locallyReadTopicRoomIds = loadPersistedLocallyReadTopicRooms()

function loadPersistedLocallyReadTopicRooms(): Set<string> {
  if (typeof localStorage === 'undefined') {
    return new Set()
  }

  try {
    const raw = localStorage.getItem(LOCALLY_READ_TOPIC_ROOMS_STORAGE_KEY)
    if (!raw) {
      return new Set()
    }

    const parsed: unknown = JSON.parse(raw)
    if (!Array.isArray(parsed)) {
      return new Set()
    }

    return new Set(
      parsed.filter((roomId): roomId is string => typeof roomId === 'string' && roomId.trim().length > 0)
        .map(roomId => roomId.trim()),
    )
  } catch {
    return new Set()
  }
}

function persistLocallyReadTopicRooms(): void {
  if (typeof localStorage === 'undefined') {
    return
  }

  try {
    localStorage.setItem(
      LOCALLY_READ_TOPIC_ROOMS_STORAGE_KEY,
      JSON.stringify([...locallyReadTopicRoomIds]),
    )
  } catch {
    // ignore quota / private mode errors
  }
}

export function rememberLocallyReadTopicRoom(roomId: string): void {
  const trimmed = roomId.trim()
  if (!trimmed) {
    return
  }

  locallyReadTopicRoomIds.add(trimmed)
  persistLocallyReadTopicRooms()
}

export function forgetLocallyReadTopicRoom(roomId: string): void {
  const trimmed = roomId.trim()
  if (!trimmed || !locallyReadTopicRoomIds.has(trimmed)) {
    return
  }

  locallyReadTopicRoomIds.delete(trimmed)
  persistLocallyReadTopicRooms()
}

export function clearLocallyReadTopicRooms(): void {
  locallyReadTopicRoomIds.clear()
  persistLocallyReadTopicRooms()
}

/** Комната помечена прочитанной этим клиентом, пока ждём подтверждение Matrix. */
export function isTopicRoomLocallyRead(roomId: string | null | undefined): boolean {
  if (!roomId?.trim()) {
    return false
  }

  return locallyReadTopicRoomIds.has(roomId.trim())
}

/**
 * Room id для optimistic zero в UI, пока Matrix notification_count ещё не догнал receipt.
 * Без этого sync/resync возвращает бейдж в навбар/title после открытия чата.
 */
export function getForceZeroTopicRoomIds(): Set<string> {
  let changed = false
  const client = getMatrixClient()
  const forceZero = new Set<string>()

  for (const roomId of [...locallyReadTopicRoomIds]) {
    const room = client?.getRoom(roomId)
    if (!room) {
      forceZero.add(roomId)
      continue
    }

    if (countMatrixRoomUnreadRaw(roomId) === 0) {
      locallyReadTopicRoomIds.delete(roomId)
      changed = true
      continue
    }

    forceZero.add(roomId)
  }

  if (changed) {
    persistLocallyReadTopicRooms()
  }

  return forceZero
}

/** Room id'ы, которые не должны быть отдельными строками в сайдбаре */
function collectHiddenMatrixRoomIds(chats: IChat[]): Set<string> {
  const hidden = new Set<string>()

  for (const chat of chats) {
    if (chat.type === CHAT_TYPES.SPACE && chat.matrix_room_id) {
      hidden.add(chat.matrix_room_id)
    }

    if (chat.space_room_id) {
      hidden.add(chat.space_room_id)
    }

    const topics = chat.topics ?? []
    for (const topic of topics) {
      if (!topic.matrix_room_id || topic.archived) {
        continue
      }
      // «Общий» = основная комната чата — её оставляем в списке
      if (topic.general) {
        continue
      }
      hidden.add(topic.matrix_room_id)
    }
  }

  return hidden
}

/** Убрать Spaces и комнаты тем из списка сайдбара */
export function filterChatsForSidebar(chats: IChat[]): IChat[] {
  const hiddenRoomIds = collectHiddenMatrixRoomIds(chats)

  return chats.filter(chat => {
    if (chat.type === CHAT_TYPES.SPACE) {
      return false
    }

    // Не фильтруем по parent_space_id: у topics-группы основная комната
    // имеет m.space.parent на технический Space — чат должен оставаться в списке.
    // Дочерние комнаты тем скрываются через collectHiddenMatrixRoomIds.

    if (chat.matrix_room_id && hiddenRoomIds.has(chat.matrix_room_id)) {
      return false
    }

    return true
  })
}

export function isTopicsModeChat(chat: Pick<IChat, 'discussion_mode'> | null | undefined): boolean {
  return chat?.discussion_mode === 'topics'
}

function getStoredTopicsDisplayMode(
  chatId: number | string | null | undefined,
): ChatTopicsDisplayMode | null {
  if (chatId == null || typeof localStorage === 'undefined') {
    return null
  }

  try {
    const value = localStorage.getItem(TOPICS_DISPLAY_STORAGE_PREFIX + String(chatId))
    return value === 'tabs' || value === 'list' ? value : null
  } catch {
    return null
  }
}

/** Личный режим отображения тем (localStorage), не общая настройка группы. */
export function setStoredTopicsDisplayMode(
  chatId: number | string | null | undefined,
  mode: ChatTopicsDisplayMode,
): void {
  if (chatId == null || typeof localStorage === 'undefined') {
    return
  }

  try {
    localStorage.setItem(TOPICS_DISPLAY_STORAGE_PREFIX + String(chatId), mode)
    topicsDisplayPreferenceRevision.value++
  } catch {
    // localStorage недоступен
  }
}

/** Личный preference → иначе fallback с чата (создание группы). */
export function resolveTopicsDisplayMode(
  chat: Pick<IChat, 'id' | 'discussion_mode' | 'topics_display_mode'> | null | undefined,
): ChatTopicsDisplayMode {
  void topicsDisplayPreferenceRevision.value
  if (!isTopicsModeChat(chat) || chat?.id == null) {
    return 'list'
  }

  return getStoredTopicsDisplayMode(chat.id)
    ?? (chat.topics_display_mode === 'tabs' ? 'tabs' : 'list')
}

/** Темы сверху чата вкладками, а не панелью в сайдбаре */
export function isTopicsTabsDisplay(
  chat: Pick<IChat, 'id' | 'discussion_mode' | 'topics_display_mode'> | null | undefined,
): boolean {
  return resolveTopicsDisplayMode(chat) === 'tabs'
}

export function sameChatId(
  left: number | string | null | undefined,
  right: number | string | null | undefined,
): boolean {
  if (left == null || right == null) {
    return false
  }

  return String(left) === String(right)
}

export function getActiveTopics(chat: IChat | null | undefined): IChatTopic[] {
  if (!chat?.topics?.length) {
    return []
  }

  return [...chat.topics]
    .filter(topic => !topic.archived)
    .sort((a, b) => a.order - b.order || a.id - b.id)
}

/** Можно открыть тему: не archived и есть matrix room. */
export function isTopicOpenable(
  topic: Pick<IChatTopic, 'archived' | 'matrix_room_id'> | null | undefined,
): boolean {
  return Boolean(topic && !topic.archived && topic.matrix_room_id?.trim())
}

export function findGeneralTopic(chat: IChat | null | undefined): IChatTopic | undefined {
  return getActiveTopics(chat).find(topic => topic.general)
}

/** Тема относится к этому чату (по room id темы или parent room). */
export function topicBelongsToChat(
  topic: Pick<IChatTopic, 'id' | 'matrix_room_id'> | null | undefined,
  chat: Pick<IChat, 'matrix_room_id' | 'topics'> | null | undefined,
): boolean {
  if (!topic || !chat) {
    return false
  }

  const topicRoomId = topic.matrix_room_id?.trim()
  if (!topicRoomId) {
    return false
  }

  if (topicRoomId === chat.matrix_room_id?.trim()) {
    return true
  }

  return (chat.topics ?? []).some(item => item.matrix_room_id?.trim() === topicRoomId)
}

/** Room для timeline / send: активная тема или основная комната чата */
export function resolveActiveMessageRoomId(state: {
  activeTopic: IChatTopic | null
  currentChat: IChat | null
  showTopicsList?: boolean
}): string | undefined {
  const topicRoomId = state.activeTopic?.matrix_room_id?.trim()
  if (topicRoomId) {
    return topicRoomId
  }

  if (state.showTopicsList && isTopicsModeChat(state.currentChat)) {
    return undefined
  }

  return state.currentChat?.matrix_room_id?.trim() || undefined
}

/** Timestamp последнего join текущего пользователя в комнату (ms). */
export function getMatrixRoomJoinedAtTs(room: Room, userId: string | null | undefined): number | null {
  if (!userId) {
    return null
  }

  const member = room.getMember(userId)
  if (!member || member.membership !== 'join') {
    return null
  }

  const memberEvent = member.events?.member
    ?? room.currentState?.getStateEvents('m.room.member', userId)
  const event = Array.isArray(memberEvent) ? memberEvent[memberEvent.length - 1] : memberEvent
  const ts = event?.getTs?.()

  return typeof ts === 'number' && Number.isFinite(ts) ? ts : null
}

function collectRoomEventsForUnreadCount(room: Room): MatrixEvent[] {
  const byId = new Map<string, MatrixEvent>()

  const add = (event: MatrixEvent | null | undefined) => {
    const eventId = event?.getId()
    if (!event || !eventId || byId.has(eventId)) {
      return
    }
    byId.set(eventId, event)
  }

  for (const event of room.getLiveTimeline().getEvents()) {
    add(event)
  }

  for (const thread of room.getThreads()) {
    for (const event of thread.events) {
      add(event)
    }
    add(thread.replyToEvent)
  }

  return [...byId.values()]
}

function isEventAfterRoomReadMarker(
  room: Room,
  readUpToId: string,
  event: MatrixEvent,
): boolean {
  const eventId = event.getId()
  if (!eventId) {
    return false
  }

  const comparison = room.compareEventOrdering(readUpToId, eventId)
  if (comparison !== null) {
    return comparison < 0
  }

  const readEvent = room.findEventById(readUpToId)
  if (!readEvent) {
    return true
  }

  return event.getTs() > readEvent.getTs()
}

function countUnreadMessagesInRoom(room: Room, myUserId: string): number {
  const readUpToId = room.getEventReadUpTo(myUserId) ?? undefined
  const joinedAtTs = getMatrixRoomJoinedAtTs(room, myUserId)
  let unread = 0

  for (const event of collectRoomEventsForUnreadCount(room)) {
    const eventId = event.getId()
    if (!eventId || event.getType() !== 'm.room.message' || event.isRedacted()) {
      continue
    }

    if (event.getSender() === myUserId) {
      continue
    }

    const eventTs = event.getTs()

    if (!readUpToId) {
      // Без read marker не считаем историю до join непрочитанной (re-invite / новый участник).
      if (joinedAtTs == null) {
        continue
      }

      if (eventTs <= joinedAtTs) {
        continue
      }

      unread++
      continue
    }

    if (!isEventAfterRoomReadMarker(room, readUpToId, event)) {
      continue
    }

    if (joinedAtTs != null && eventTs <= joinedAtTs) {
      continue
    }

    unread++
  }

  return unread
}

/**
 * Непрочитанные из Matrix SDK без учёта локального «уже прочитал».
 * Нужен для prune locally-read и reconcile после receipt.
 */
export function countMatrixRoomUnreadRaw(roomId: string): number {
  const client = getMatrixClient()
  if (!client) {
    return 0
  }

  const room = client.getRoom(roomId)
  if (!room) {
    return 0
  }

  const myUserId = client.getUserId()
  if (!myUserId) {
    return 0
  }

  const readUpToId = room.getEventReadUpTo(myUserId) ?? undefined
  const joinedAtTs = getMatrixRoomJoinedAtTs(room, myUserId)

  try {
    const totalCount = room.getUnreadNotificationCount?.(NotificationCountType.Total) ?? 0
    const highlightCount = room.getUnreadNotificationCount?.() ?? 0
    const serverCount = Math.max(totalCount, highlightCount)

    if (serverCount > 0) {
      // Synapse для нового участника часто отдаёт unread по истории — пересчитываем с cutoff join.
      if (!readUpToId && joinedAtTs != null) {
        return countUnreadMessagesInRoom(room, myUserId)
      }

      if (readUpToId) {
        const timelineUnread = countUnreadMessagesInRoom(room, myUserId)
        if (timelineUnread === 0 && isMatrixReadMarkerAtLastReadable(room, myUserId)) {
          return 0
        }

        return serverCount
      }

      return serverCount
    }
  } catch {
    // fallback ниже
  }

  try {
    return countUnreadMessagesInRoom(room, myUserId)
  } catch {
    return 0
  }
}

/**
 * Непрочитанные для UI. Overlay не маскирует raw > 0 — иначе второй ПК видит правду, этот врёт.
 */
export function countMatrixRoomUnread(roomId: string): number {
  const raw = countMatrixRoomUnreadRaw(roomId)
  if (raw === 0) {
    forgetLocallyReadTopicRoom(roomId)
  }

  return raw
}

function findTopicIndexWithLatestActivity(topics: IChatTopic[]): number {
  const client = getMatrixClient()
  if (!client) {
    return topics.findIndex(topic => topic.general)
  }

  let bestIdx = -1
  let bestTs = -1

  for (let index = 0; index < topics.length; index++) {
    const topic = topics[index]
    if (topic.archived || !topic.matrix_room_id) {
      continue
    }

    const room = client.getRoom(topic.matrix_room_id)
    if (!room) {
      continue
    }

    const lastMessage = getMatrixRoomLastMessage(room)
    const ts = lastMessage?.created_at ? Date.parse(lastMessage.created_at) : 0
    if (!Number.isNaN(ts) && ts > bestTs) {
      bestTs = ts
      bestIdx = index
    }
  }

  if (bestIdx >= 0) {
    return bestIdx
  }

  return topics.findIndex(topic => topic.general)
}

/** Суммарный unread группы тем: только Matrix, без chat.unread_count (избегаем feedback loop). */
export function computeTopicsGroupUnreadCount(
  chat: Pick<IChat, 'matrix_room_id'>,
  topics: IChatTopic[],
  forceZeroRoomIds?: Iterable<string>,
): number {
  const forceZero = new Set(
    [...(forceZeroRoomIds ?? [])]
      .map(roomId => roomId.trim())
      .filter(Boolean),
  )
  const parentRoomId = chat.matrix_room_id?.trim()
  const parentRoomUnread = parentRoomId && !forceZero.has(parentRoomId)
    ? countMatrixRoomUnread(parentRoomId)
    : 0
  const topicsSum = topics.reduce((sum, topic) => sum + (topic.unread_count ?? 0), 0)

  return Math.max(parentRoomUnread, topicsSum)
}

export type TopicUnreadSource =
  | 'stored'
  | 'matrix-topic-room'
  | 'matrix-parent-room'
  | 'none'

/** Детализация источника unread для диагностики бейджей тем */
export function explainTopicUnreadCount(
  topic: IChatTopic,
  parentChat?: Pick<IChat, 'matrix_room_id' | 'unread_count'> | null,
): {
    count: number
    source: TopicUnreadSource
    storedUnread: number
    matrixTopicUnread: number
    matrixParentUnread: number
  } {
  const storedUnread = topic.unread_count ?? 0
  const topicRoomId = topic.matrix_room_id?.trim()
  const matrixTopicUnread = topicRoomId ? countMatrixRoomUnread(topicRoomId) : 0
  const parentRoomId = parentChat?.matrix_room_id?.trim()
  const matrixParentUnread = parentRoomId ? countMatrixRoomUnread(parentRoomId) : 0

  if (storedUnread > 0) {
    return {
      count: storedUnread,
      source: 'stored',
      storedUnread,
      matrixTopicUnread,
      matrixParentUnread,
    }
  }

  if (matrixTopicUnread > 0) {
    return {
      count: matrixTopicUnread,
      source: 'matrix-topic-room',
      storedUnread,
      matrixTopicUnread,
      matrixParentUnread,
    }
  }

  if (topic.general && parentRoomId && matrixParentUnread > 0) {
    return {
      count: matrixParentUnread,
      source: 'matrix-parent-room',
      storedUnread,
      matrixTopicUnread,
      matrixParentUnread,
    }
  }

  return {
    count: 0,
    source: 'none',
    storedUnread,
    matrixTopicUnread,
    matrixParentUnread,
  }
}

/** Сводка unread по темам для логов (фильтр: chat-matrix topics unread) */
export function buildTopicsUnreadDebugBreakdown(
  chat: IChat,
  options?: { resolvedTopics?: IChatTopic[]; isActiveTopicId?: number | null },
): Record<string, unknown> {
  const resolvedTopics = options?.resolvedTopics ?? resolveTopicUnreadCounts(chat)
  const activeTopics = getActiveTopics({ ...chat, topics: resolvedTopics })
  const parentRoomId = chat.matrix_room_id?.trim()
  const parentRoomUnread = parentRoomId ? countMatrixRoomUnread(parentRoomId) : 0
  const topicsFromStore = getActiveTopics(chat)

  return {
    chatId: chat.id,
    chatStoredUnread: chat.unread_count ?? 0,
    parentRoomId: parentRoomId ?? null,
    parentRoomUnread,
    groupComputedUnread: computeTopicsGroupUnreadCount(chat, resolvedTopics),
    resolvedTopicsSum: resolvedTopics.reduce((sum, topic) => sum + (topic.unread_count ?? 0), 0),
    topics: activeTopics.map(topic => {
      const explanation = explainTopicUnreadCount(topic, chat)
      const storeTopic = topicsFromStore.find(item => item.id === topic.id)
      return {
        id: topic.id,
        title: topic.title,
        general: topic.general,
        matrix_room_id: topic.matrix_room_id ?? null,
        storedUnreadBeforeResolve: storeTopic?.unread_count ?? 0,
        resolvedUnread: topic.unread_count ?? 0,
        computedUnread: explanation.count,
        unreadSource: explanation.source,
        matrixTopicUnread: explanation.matrixTopicUnread,
        matrixParentUnread: explanation.matrixParentUnread,
        badgeWouldShow: explanation.count > 0 && options?.isActiveTopicId !== topic.id,
        isActiveTopic: options?.isActiveTopicId === topic.id,
      }
    }),
  }
}

/** Пересчитывает unread_count для каждой темы только из Matrix (не из chat.unread_count). */
export function resolveTopicUnreadCounts(
  chat: IChat,
  options?: { debugLabel?: string; forceZeroRoomIds?: Iterable<string> },
): IChatTopic[] {
  const topics = getActiveTopics(chat)
  if (!topics.length) {
    return chat.topics ?? []
  }

  const forceZeroRoomIds = new Set(
    [...(options?.forceZeroRoomIds ?? [])]
      .map(roomId => roomId.trim())
      .filter(Boolean),
  )
  const parentRoomId = chat.matrix_room_id?.trim()
  const parentRoomUnread = parentRoomId && !forceZeroRoomIds.has(parentRoomId)
    ? countMatrixRoomUnread(parentRoomId)
    : 0
  const perTopicMatrix: Array<Record<string, unknown>> = []

  const resolved = topics.map(topic => {
    const topicRoomId = topic.matrix_room_id?.trim()
    const topicRoomUnread = topicRoomId && !forceZeroRoomIds.has(topicRoomId)
      ? countMatrixRoomUnread(topicRoomId)
      : 0
    let resolvedUnread = topicRoomUnread
    let resolveMode: 'topic-room' | 'general-parent-room' | 'forced-zero' = 'topic-room'

    if (topicRoomId && forceZeroRoomIds.has(topicRoomId)) {
      resolvedUnread = 0
      resolveMode = 'forced-zero'
    } else if (topic.general && parentRoomId && (!topicRoomId || topicRoomId === parentRoomId)) {
      resolvedUnread = parentRoomUnread
      resolveMode = 'general-parent-room'
    }

    perTopicMatrix.push({
      id: topic.id,
      title: topic.title,
      general: topic.general,
      matrix_room_id: topicRoomId ?? null,
      topicRoomUnread,
      resolveMode,
      resolvedUnreadBeforeOrphan: resolvedUnread,
      previousStoredUnread: topic.unread_count ?? 0,
    })

    return {
      ...topic,
      unread_count: resolvedUnread,
    }
  })

  const topicsUnreadTotal = resolved.reduce((sum, topic) => sum + (topic.unread_count ?? 0), 0)
  let orphanUnread = 0
  let orphanTargetTopicId: number | null = null

  if (parentRoomUnread > topicsUnreadTotal) {
    orphanUnread = parentRoomUnread - topicsUnreadTotal
    const targetIndex = findTopicIndexWithLatestActivity(topics)
    if (targetIndex >= 0) {
      orphanTargetTopicId = resolved[targetIndex].id
      resolved[targetIndex] = {
        ...resolved[targetIndex],
        unread_count: (resolved[targetIndex].unread_count ?? 0) + orphanUnread,
      }
    }
  }

  const finalTopics = (chat.topics ?? []).map(topic => {
    if (topic.archived) {
      return topic
    }

    const resolvedTopic = resolved.find(item => item.id === topic.id)
    return resolvedTopic ?? topic
  })

  if (options?.debugLabel) {
    topicsDebugLog(`utils · resolveTopicUnreadCounts · ${options.debugLabel}`, {
      chatId: chat.id,
      parentRoomId: parentRoomId ?? null,
      parentRoomUnread,
      topicsUnreadTotalBeforeOrphan: topicsUnreadTotal,
      orphanUnread,
      orphanTargetTopicId,
      perTopicMatrix,
      finalTopics: finalTopics
        .filter(topic => !topic.archived)
        .map(topic => ({
          id: topic.id,
          title: topic.title,
          unread_count: topic.unread_count ?? 0,
          matrix_room_id: topic.matrix_room_id ?? null,
        })),
    })
  }

  return finalTopics
}

/** Непрочитанные в теме: stored count, локально прочитанные, Matrix room или parent-room группы */
export function getTopicUnreadCount(
  topic: IChatTopic,
  parentChat?: Pick<IChat, 'matrix_room_id' | 'unread_count'> | null,
): number {
  const topicRoomId = topic.matrix_room_id?.trim()
  if (topicRoomId && isTopicRoomLocallyRead(topicRoomId) && countMatrixRoomUnreadRaw(topicRoomId) === 0) {
    return 0
  }

  if ((topic.unread_count ?? 0) > 0) {
    return topic.unread_count ?? 0
  }

  return explainTopicUnreadCount(topic, parentChat).count
}

export function findChatTopicByRoomId(
  chats: IChat[],
  roomId: string,
): { chat: IChat; chatIndex: number; topicIndex: number } | null {
  for (let chatIndex = 0; chatIndex < chats.length; chatIndex++) {
    const chat = chats[chatIndex]
    const topicIndex = chat.topics?.findIndex(topic => topic.matrix_room_id === roomId) ?? -1
    if (topicIndex >= 0) {
      return { chat, chatIndex, topicIndex }
    }
  }

  return null
}
